import type { FC } from "react";

import {
  SONNER_CONTENT_CLASS,
  SONNER_DEFAULTS,
  SONNER_DESCRIPTION_CLASS,
  SONNER_ICON_CLASS,
  SONNER_ICON_LOADING_CLASS,
  SONNER_ICON_MAP,
  SONNER_LOADING_ICON,
  SONNER_ROOT_CLASS,
  SONNER_ROOT_FOCUS_CLASS,
  SONNER_VARIANT_CLASS_MAP,
  type TSonnerProps,
} from "@/ui/components/feedback/Sonner/Sonner.config";
import { SonnerClose } from "@/ui/components/feedback/Sonner/SonnerClose";
import { Icon } from "@/ui/components/primitives/Icon";
import { TextBody } from "@/ui/components/typography/TextBody";
import { cn } from "@/ui/lib/utils";
import { SonnerTitle } from "./SonnerTitle";
import { Inline, Stack } from "../../layout";

export const Sonner: FC<TSonnerProps> = (
  {
    title,
    description,
    detail,
    variant = SONNER_DEFAULTS.variant,
    loading = SONNER_DEFAULTS.loading,
    action,
    onDismiss,
    dismissLabel,
    className,
    role = SONNER_DEFAULTS.role,
    tabIndex = SONNER_DEFAULTS.tabIndex,
    "aria-live": ariaLive = "polite",
    ...props
  },
) => {
  const iconKey = loading ? SONNER_LOADING_ICON : SONNER_ICON_MAP[variant];

  return (
    <div
      {...props}
      data-component="Sonner"
      role={role}
      tabIndex={tabIndex}
      aria-live={ariaLive}
      aria-atomic="true"
      aria-busy={loading || undefined}
      className={cn(
        SONNER_ROOT_CLASS,
        SONNER_ROOT_FOCUS_CLASS,
        SONNER_VARIANT_CLASS_MAP[variant],
        className,
      )}
    >
      <Stack gap="2">
        <Inline gap="2" className="items-center justify-between flex-1">
          <Inline gap="2" className="items-center">
            { iconKey ? (
              <Icon
                icon={iconKey}
                aria-hidden="true"
                className={cn(SONNER_ICON_CLASS, loading && SONNER_ICON_LOADING_CLASS)}
              />
            ) : null }

            <SonnerTitle>{ title }</SonnerTitle>
          </Inline>
          { onDismiss ? (
            <SonnerClose aria-label={dismissLabel} onClick={onDismiss} className="self-end"/>
          ) : null }
        </Inline>

        <div
          className={cn(
            SONNER_CONTENT_CLASS,
            iconKey ? "ml-8" : null,
          )}
        >
          { description ? (
            <TextBody size={2} color="inherit" isResponsive={false} className={SONNER_DESCRIPTION_CLASS}>{ description }</TextBody>
          ) : null }
          { detail ? (
            <TextBody size={3} color="secondary" isResponsive={false}>{ detail }</TextBody>
          ) : null }
        </div>
      </Stack>

    </div>
  );
};

Sonner.displayName = "Sonner";
